import { Injectable, inject } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
	ConfigurationValue,
	DataleanBaseApiService,
	DataleanPagedResult,
	DataleanSelectOption,
	FeatureValue,
	FeatureValueType,
	ModalService,
	PaginationInfo,
	Parts,
	SimpleObject,
	SortInfo,
	Structure,
	StructureType,
} from 'addiction-components';
import { Observable, Subject, forkJoin } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { HeaderLocaleService } from 'src/app/components';
import { CommunitySelectSelectors } from 'src/app/core/state/app.selectors';
import { ImportExportDialogComponent } from 'src/app/shared/components/import-export-dialog/import-export-dialog.component';
import { Product } from 'src/app/shared/models';
import { environment } from 'src/environments/environment';
import { ProductActions } from '../../../core/state/app.actions';

@Injectable({ providedIn: 'root' })
export class ProductService {
	private store = inject(Store);
	private translateService = inject(TranslateService);
	private headerSrv = inject(HeaderLocaleService);
	private baseApi = inject(DataleanBaseApiService);
	private modalSrv = inject(ModalService);
	private translate = inject(TranslateService);
	private destroyed$ = new Subject();
	private lastCommunitySelected$ = this.store.select(CommunitySelectSelectors.selectLastCommunitySelected).pipe(takeUntil(this.destroyed$));
	private lastCommunitySelected: string | undefined = undefined;

	constructor() {}

	init(): void {
		this.lastCommunitySelected$.pipe(takeUntil(this.destroyed$)).subscribe((communityUUID) => {
			this.lastCommunitySelected = communityUUID;
			this.getStructureAndFeatures(communityUUID);
		});
	}

	destroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	getStructureAndFeatures(communityUUID?: string) {
		const structureAdditionalParams: {
			type?: StructureType;
		} = { type: StructureType.PRODUCT };

		const featureAdditionalParams: {
			featureValueCommunityUUID?: string;
			type?: FeatureValueType;
			locale?: string;
		} = { type: FeatureValueType.PRODUCT, locale: this.translateService.currentLang ?? this.translateService.defaultLang };

		//TODO rimttere quando è possibile prendere le categorie in modo decente filtrate per community
		// if (communityUUID !== undefined) {
		// 	featureAdditionalParams.featureValueCommunityUUID = communityUUID;
		// } else if (this.lastCommunitySelected !== undefined) {
		// 	featureAdditionalParams.featureValueCommunityUUID = this.lastCommunitySelected;
		// }

		this.baseApi
			.getMany<Structure>(environment.structureUrl, [Parts.STRUCTURE_FIELDS], { additionalParams: structureAdditionalParams })
			.subscribe((structures) => {
				this.store.dispatch(ProductActions.setStructures({ structures }));
			});
		this.baseApi
			.getMany<FeatureValue>(environment.featureValueUrl, [Parts.FEATURE_VALUES], {
				additionalParams: featureAdditionalParams,
			})
			.subscribe((features) => {
				this.store.dispatch(ProductActions.setFeatures({ features }));
			});
	}

	getProducts(options: {
		parts?: Parts[];
		locale?: string;
		pagination?: PaginationInfo;
		sort?: SortInfo;
		filters?: { uuids?: string[]; structureUUID?: string; query?: string; communityUUID?: string };
	}) {
		const { filters, parts, locale, pagination, sort } = options;

		const params: SimpleObject = {
			locale: locale ?? this.headerSrv.getActiveLocale(),
			active: true,
		};

		// TODO: generalizzare questa logica in una funzione reutilizzabile
		if (filters?.structureUUID) params['structureUUID'] = filters.structureUUID;
		if (filters?.query) {
			params['q'] = filters.query;
			params['searchFields'] = ['name']; // parametrizzare i campi di ricerca??
		}
		if (filters?.communityUUID) params['communityUUID'] = filters.communityUUID;

		if (filters?.uuids) params['uuid'] = filters.uuids.join(',');

		return this.baseApi.getManyPaged<Product>(environment.productUrl, parts ?? [Parts.ALL], {
			pagination,
			sort,
			additionalParams: params,
		});
	}

	getStructures(parts: Parts[]) {
		if (!parts) parts = [Parts.EMPTY];

		return this.baseApi.getManyPaged<Structure>(environment.structureUrl, parts);
	}

	countProducts(filters: { structureUUID?: string; communityUUID?: string; q?: string; searchFields?: string }) {
		return this.baseApi.getOne<{ count: number }>(environment.productUrl + 'count', '', [], {
			organizationUUID: environment.organizationUUID,
			...filters,
		});
	}

	getProductVersioning(productUUID: string, pages: number[], locale: string, sort?: Sort): Observable<DataleanPagedResult<Product>[]> {
		const obs: Observable<DataleanPagedResult<Product>>[] = [];

		const params: {
			productUUID: string;
			locale: string;
			sortBy?: string;
		} = {
			locale: locale ?? this.headerSrv.getActiveLocale(),
			productUUID: productUUID,
		};

		if (productUUID) params.productUUID = productUUID;
		if (sort) params.sortBy = `${sort.active}#${sort.direction}`;

		for (const page of pages) {
			const pagination: PaginationInfo = new PaginationInfo(environment.pageSize, page);

			obs.push(
				this.baseApi.getManyPaged<Product>(
					`${environment.productUrl}${productUUID}/versions`,
					[Parts.STATUS, Parts.CREATE_DATE, Parts.CREATED_BY, Parts.ENTITY_VERSION],
					{
						pagination,
						additionalParams: params,
					}
				)
			);
		}
		return forkJoin(obs);
	}

	public multipleUpdate(bulkPayload: { uuidList: string[]; delete?: boolean; active?: boolean }) {
		return this.baseApi.updateBulk(environment.productUrl + 'multipleUpdate', bulkPayload).pipe(map(() => true));
	}

	fetchProducts(
		pages: number[],
		sort?: Sort,
		locale?: string,
		gridSearch?: string | null,
		filters?: { featureValue?: string[]; structureUUID?: string; communityUUID?: string; active?: boolean; status?: string | undefined },
		hasVersioning?: ConfigurationValue | null
	): Observable<DataleanPagedResult<Product>[]> {
		const obs: Observable<DataleanPagedResult<Product>>[] = [];

		const params: {
			sortBy?: string;
			q?: string;
			searchFields?: string;
			locale?: string;
			active?: boolean;
			structureUUID?: string;
			featureValueList?: string;
			communityUUID?: string;
			status?: string;
		} = {
			locale: locale ?? this.headerSrv.getActiveLocale(),
		};

		// TODO: generalizzare questa logica in una funzione reutilizzabile
		if (filters?.structureUUID) params['structureUUID'] = filters.structureUUID;

		if (filters?.featureValue?.length) params['featureValueList'] = filters.featureValue.join(',') + '#and';

		if (filters?.active !== undefined) params['active'] = filters.active;

		if (filters?.communityUUID) params['communityUUID'] = filters.communityUUID;

		if (filters?.status) params['status'] = filters.status;

		//SORT
		if (sort) params.sortBy = `${sort.active}#${sort.direction}`;
		if (gridSearch) {
			params.q = gridSearch;
			params.searchFields = 'name,description';
		}

		for (const page of pages) {
			const pagination: PaginationInfo = new PaginationInfo(environment.pageSize, page);

			obs.push(
				this.baseApi.getManyPaged<Product>(
					environment.productUrl,
					[Parts.FEATURE_VALUE_LIST, Parts.LESSON_CONTENT_LIST, Parts.UPDATE_DATE, ...(hasVersioning ? [Parts.ENTITY_VERSION] : [])],
					{
						pagination,
						additionalParams: params,
					}
				)
			);
		}
		return forkJoin(obs);
	}

	openImportExportModal(structures: DataleanSelectOption<string>[] | null | undefined) {
		const $openModalRes = this.modalSrv.openDialog(
			ImportExportDialogComponent,
			{ title: this.translate.instant('MODALS.IMPORT_EXPORT.MODAL_TITLE') },
			{
				showExport: true,
				showImport: true,
				endpointImport: environment.productUrl + 'import/',
				endpointExport: environment.productUrl + 'export/',
				structures: structures,
				negativeReflection: true,
			}
		);
		return $openModalRes.result$;
	}
}
